import { format } from 'date-fns';
import { SuperApiClaimOwnerCapacity } from '@dispatch/shared/types';
import {
  CapacityAxis,
  CapacityDay,
  CapacityWeek,
  buildCapacityModel,
} from './CapacityBand';

/**
 * Hover tooltip for the Claim Owner capacity band (ADO #104914, AC 28).
 *
 * ONE MODEL. Every figure here comes out of `buildCapacityModel` — the same
 * call, over the same seven-day window, that drew the band. Nothing in this
 * file recomputes availability, the `min(daily, weekly)` rule, or the elapsed
 * clause; it reads `day.cap`, `day.used`, `day.remaining` and the day's own
 * `week` and formats them. Two surfaces printing different numbers for the
 * same resource and date is the defect this arrangement exists to prevent, so
 * a parity test pins the tooltip's figures to the band's.
 *
 * AUGMENTS, NEVER REPLACES. This is wired to Bryntum's `getText`, which the
 * ScheduleTooltip feature appends to its own clock/date readout inside
 * `.b-sch-hover-tip-msg`. `generateTipContent` is the other hook and it builds
 * the WHOLE tooltip (`clockHtml + messageHtml`), so overriding that would
 * delete the default readout the criterion requires this to sit alongside.
 *
 * SCOPED TO THE CELL, NOT THE ROW. The figures are drawn on the time axis, so
 * the same row also carries the task bars — hovering those must give the
 * default readout and nothing else. The scope test is `target.closest()` on
 * the element the tooltip hands in. A separately tracked pointer position is
 * always stale here: Bryntum's own mousemove handler runs before ours, so a
 * tracked coordinate describes the PREVIOUS move and capacity content leaks
 * onto the task row.
 *
 * NO TIME ZONE. The figures are calculated in the zone the dispatcher picked
 * from the toolbar, so naming it back at them says nothing they did not just
 * say. `model.timeZoneId` is deliberately never read here.
 */

/** Attribute the band stamps on every capacity cell — the date it covers. */
export const CAPACITY_DATE_ATTR = 'data-capacity-date';
/** Attribute the band stamps on every capacity cell — the resource it belongs to. */
export const CAPACITY_RESOURCE_ATTR = 'data-capacity-resource';
/** Attribute the band stamps on every weekly bar — the Monday of the week it covers. */
export const CAPACITY_WEEK_ATTR = 'data-capacity-week';

/** Any element inside a capacity cell resolves to the cell through this. */
const CAPACITY_CELL_SELECTOR = `[${CAPACITY_DATE_ATTR}][${CAPACITY_RESOURCE_ATTR}]`;
/** Any element inside a weekly bar resolves to the bar through this. */
const CAPACITY_WEEK_SELECTOR = `[${CAPACITY_WEEK_ATTR}][${CAPACITY_RESOURCE_ATTR}]`;

const DAY_LABEL_FORMAT = 'EEE MM/dd';
const SHORT_DATE_FORMAT = 'MM/dd';

/** The subset of an `Element` this needs — kept narrow so tests can be plain. */
interface ClosestCapable {
  closest(selector: string): {
    getAttribute(name: string): string | null;
  } | null;
}

function asClosestCapable(target: unknown): ClosestCapable | null {
  if (!target || typeof target !== 'object') return null;
  const candidate = target as { closest?: unknown };
  return typeof candidate.closest === 'function'
    ? (target as ClosestCapable)
    : null;
}

export interface CapacityTooltipOptions {
  /**
   * The hovered element, as handed in by Bryntum (`event.target`). Anything
   * without a `closest` — null, the document, a detached target — resolves to
   * no cell and so to no capacity content.
   */
  target: unknown;
  /**
   * The resource the tooltip resolved the hover to. A cell naming a DIFFERENT
   * resource is refused rather than answered: showing one claim owner's
   * figures under another's name is worse than showing none.
   */
  resourceId: string | null | undefined;
  capacity: SuperApiClaimOwnerCapacity | null | undefined;
  /** Rendered axis geometry; its `startDate` is the band's window start. */
  axis: CapacityAxis | null | undefined;
  /** Injectable for tests, exactly as `buildCapacityModel` takes it. */
  today?: Date;
  /** The board's selected time zone — the zone "today" resolves in. */
  timeZone?: string;
}

/** Zero availability is the one figure that gets colour; no tone ladder here. */
function noneCls(remaining: number): string {
  return remaining === 0 ? ' none' : '';
}

function dayLineHtml(day: CapacityDay): string {
  const date = `<span class="capacity-tip-date">${format(
    day.date,
    DAY_LABEL_FORMAT
  )}</span>`;

  // An unserved day is answered BEFORE the isOff branch, which it also sets.
  // Saying "no capacity" for a date the payload never carried states
  // unavailability, which AC 31 forbids for a missing date — and it is the one
  // reading a dispatcher must not take, because the resource may be fully
  // available that day. The band shows a subdued dash for the same reason.
  if (day.isMissing) {
    return (
      '<div class="capacity-tip-row">' +
      date +
      '<span class="capacity-tip-nodata">no capacity data yet</span>' +
      '</div>'
    );
  }

  // A past or zero-cap day carries no figures in the band either — it is an em
  // dash and "off" there, so it must not become "0 of 0" here.
  if (day.isOff) {
    return (
      '<div class="capacity-tip-row">' +
      date +
      '<span class="capacity-tip-off">no capacity</span>' +
      '</div>'
    );
  }

  return (
    '<div class="capacity-tip-row">' +
    date +
    // Used before cap, matching the band's flipped day figure so the hover and
    // the cell it describes read in the same order.
    '<span class="capacity-tip-figures">used ' +
    `<b class="capacity-tip-used">${day.used}</b>, cap ` +
    `<b class="capacity-tip-cap">${day.cap}</b></span>` +
    `<span class="capacity-tip-remaining${noneCls(day.remaining)}">` +
    `<b class="capacity-tip-remaining-value">${day.remaining}</b> remaining` +
    '</span>' +
    '</div>'
  );
}

/**
 * The weekly figure.
 *
 * The FIGURES TEXT is deliberately the same sentence the band's weekly bar
 * prints — `used of cap · remaining remaining` plus the elapsed clause — so
 * the two can be compared character for character by a test. Only the class
 * names differ, because the two live in different chrome. The numbers are read
 * straight off the week object, including `usedEarlierThisWeek`, which the
 * model documents as the one field both surfaces read so neither re-derives
 * the rule behind it.
 */
function weekLineHtml(week: CapacityWeek): string {
  const range =
    '<span class="capacity-tip-week-range">' +
    `Mon ${format(week.start, SHORT_DATE_FORMAT)} &ndash; Sun ${format(
      week.end,
      SHORT_DATE_FORMAT
    )}` +
    '</span>';

  // A week wholly behind today reports nothing, exactly as the band's bar does, and in the band's
  // words — AC 24 asks this line to state that the week has gone by rather than that there is no
  // capacity, which is a different cause with a different remedy. The range still names WHICH week,
  // because on its own the phrase would not say.
  if (!week.hasFutureDays) {
    return (
      '<div class="capacity-tip-row capacity-tip-week">' +
      range +
      '<span class="capacity-tip-week-figures">' +
      '<span class="capacity-tip-off">week elapsed</span>' +
      '</span>' +
      '</div>'
    );
  }

  const elapsed = week.usedEarlierThisWeek
    ? ` <span class="capacity-tip-week-elapsed">(${week.usedEarlierThisWeek} used earlier this week)</span>`
    : '';
  return (
    '<div class="capacity-tip-row capacity-tip-week">' +
    range +
    '<span class="capacity-tip-week-figures">' +
    `${week.used} of ${week.cap} &middot; ` +
    `<span class="capacity-tip-week-remaining${noneCls(week.remaining)}">` +
    `${week.remaining} remaining</span>${elapsed}` +
    '</span>' +
    '</div>'
  );
}

/**
 * Capacity content for a hovered element, or `''` for anything that is not a
 * capacity cell.
 *
 * Empty string is the important return: Bryntum's `getText` contract is that a
 * falsy result appends no message at all, leaving its clock/date readout
 * exactly as it was. Every refusal below — not a cell, another resource's
 * cell, no axis yet, no capacity block, a date the window does not hold —
 * lands there, so the task row can never pick up capacity content.
 */
export function capacityTooltipText({
  target,
  resourceId,
  capacity,
  axis,
  today,
  timeZone,
}: CapacityTooltipOptions): string {
  const element = asClosestCapable(target);

  // A day cell first. The weekly bar is checked only when the hover did not land on one, because a
  // cell is the more specific target and the two never nest.
  const cell = element?.closest(CAPACITY_CELL_SELECTOR);
  const weekBar = cell ? null : element?.closest(CAPACITY_WEEK_SELECTOR);
  if (!cell && !weekBar) return '';

  const owner = cell ?? weekBar!;
  const ownerResourceId = owner.getAttribute(CAPACITY_RESOURCE_ATTR);
  // The element names its own resource; the tooltip was told one separately. They
  // agree in every normal hover, and when they do not the hover has been
  // resolved to the wrong row — refuse rather than answer for someone else.
  if (!ownerResourceId || !resourceId || ownerResourceId !== resourceId)
    return '';

  if (!axis) return '';
  // `today` is forwarded, never defaulted here: `buildCapacityModel` already
  // owns that default, and a second one would be a second definition of now.
  const model = buildCapacityModel(capacity, axis.startDate, today, timeZone);
  if (!model) return '';

  // The weekly row answers with the week alone. There is no day line, because the bar spans up to
  // seven of them and naming any one would be arbitrary -- and no gate line, since that clause
  // explains why a particular DAY is capped, which is not the question being asked here.
  if (weekBar) {
    const weekKey = weekBar.getAttribute(CAPACITY_WEEK_ATTR);
    if (!weekKey) return '';

    const week = model.weeks.find((candidate) => candidate.key === weekKey);
    if (!week) return '';

    return `<div class="capacity-tip">${weekLineHtml(week)}</div>`;
  }

  const dateKey = owner.getAttribute(CAPACITY_DATE_ATTR);
  if (!dateKey) return '';

  const day = model.days.find((candidate) => candidate.key === dateKey);
  if (!day) return '';

  const gate = day.isWeeklyGated
    ? '<div class="capacity-tip-gate">Weekly cap reached &mdash; no capacity ' +
      'remains this week.</div>'
    : '';

  return (
    '<div class="capacity-tip">' +
    dayLineHtml(day) +
    (day.week ? weekLineHtml(day.week) : '') +
    gate +
    '</div>'
  );
}
